import brandConfig from '../generated/brand-config.json'
import { generateDeviceDataKey } from '../Components/ReusableComponents/ByteTransfer/utils'
import { isVersionAbove } from '../utils/verionControl'
import {
    logo_company,
    logo_company_compact,
    logo_primary,
    logo_compact,
    logo_icon_svg,
    logo_favicon,
    home_illustration,
} from '../generated/brand-logos'

import { externalImages } from '../generated/external-images'
import {
    deviceImages,
    deviceInAppImages,
    devicePinoutImages,
    accessoryImages,
    expansionKitImages,
    kitImages,
} from '../generated/device-images'

import defaultLogoPrimary from '../Assets/images/plodelogo.svg'
import defaultLogoCompany from '../Assets/Bisoft_UI/Main/PNG/yuduRobotics.png'
import defaultLogoCompanyCompact from '../Assets/yudulogo.png'
import defaultLogoCompact from '../Assets/Bisoft_UI/Main/SVG/plodeIcon.svg'
import defaultLogoIconSvg from '../Assets/Bisoft_UI/Main/SVG/plodeIconSvg.svg'
import defaultHomeIllustration from '../Assets/Bisoft_UI/Main/PNG/homePage_asset@2x.png'

export function getBrandConfig() {
    return brandConfig
}

export function getBrand() {
    return brandConfig.brand
}

export function getEnabledDevices() {
    return (brandConfig.devices || []).filter((d) => d.enabled)
}

export function getDeviceByKey(deviceKey) {
    return (brandConfig.devices || []).find((d) => d.device_key === deviceKey)
}

// Kit-selection catalog: categories + item placement (devices/accessories).
export function getCategories() {
    return (brandConfig.categories || [])
        .filter((c) => c.enabled !== false)
        .slice()
        .sort((a, b) => (a.order || 0) - (b.order || 0))
}

export function getCatalogItems() {
    return brandConfig.catalogItems || []
}

export function getCategoryItems(categorySlug) {
    return getCatalogItems().filter(
        (it) => it.enabled !== false && it.category_slug === categorySlug
    )
}

export function getCatalogItem(slug) {
    return getCatalogItems().find((it) => it.slug === slug)
}

export function getDeviceKits(deviceKey) {
    const device = getDeviceByKey(deviceKey)
    return ((device && device.kits) || []).filter((k) => k.enabled !== false)
}

export function getDeviceCardSrc(deviceKey) {
    const device = getDeviceByKey(deviceKey)
    return device ? device.src : undefined
}

function findKit(slug) {
    for (const d of brandConfig.devices || []) {
        const kit = (d.kits || []).find((k) => k.slug === slug)
        if (kit) return kit
    }
    return null
}

export function getKitHeroImage(slug) {
    const mod = kitImages[slug]
    if (mod) return mod.default || mod
    const kit = findKit(slug)
    if (kit && kit.hero_image && /^https?:\/\//.test(kit.hero_image))
        return kit.hero_image
    return null
}

export function getAccessorySupportedDevices(slug) {
    const it = getCatalogItem(slug)
    return (it && it.supported_devices) || []
}

export function getPeeCeeVariantLabel(variant) {
    const it = getCatalogItems().find((x) => x.peecee_variant === variant)
    return (it && it.display_name) || variant
}

// Expansion kits: brand-declared catalog items (type: expansion_kit). The card
// list is still supplied by the backend, but the brand config can override the
// display name / hero image and hide a kit (enabled: false), keyed by
// expansion_kit_key (which equals the backend kit id). Returns every declared
// item (including disabled ones) so callers can decide how to merge.
export function getExpansionKits() {
    return getCatalogItems().filter((it) => it.type === 'expansion_kit')
}

export function getExpansionKit(key) {
    return getExpansionKits().find((it) => it.expansion_kit_key === key)
}

// Brand hero image for an expansion kit, keyed by expansion_kit_key. Returns a
// bundled/uploaded brand image when set, else a brand URL, else null so the
// caller falls back to the backend-delivered image.
export function getExpansionKitHeroImage(key) {
    const mod = expansionKitImages[key]
    if (mod) return mod.default || mod
    const it = getExpansionKit(key)
    if (it && it.hero_image && /^https?:\/\//.test(it.hero_image))
        return it.hero_image
    return null
}

// Brand hero image for an accessory item (AI0/AI1...), keyed by accessory_key.
export function getAccessoryHeroImage(accessoryKey) {
    const mod = accessoryImages[accessoryKey]
    if (mod) return mod.default || mod
    const it = getCatalogItems().find(
        (x) => x.type === 'accessory' && x.accessory_key === accessoryKey
    )
    if (it && it.hero_image && /^https?:\/\//.test(it.hero_image))
        return it.hero_image
    return null
}

export function getDeviceDisplayName(deviceKey) {
    const device = getDeviceByKey(deviceKey)
    return device ? device.display_name : deviceKey
}

const DEVICE_NAME_ALIASES = {
    Ace: 'Ace1',
    Playcomputer: 'Ace1',
    Zing: 'Humanoid',
    Crawlee: 'Hexapod',
    Atr_h2: 'Atr',
}

export function getBrandDeviceName(deviceIdOrKey) {
    if (!deviceIdOrKey) return deviceIdOrKey
    const normalized = DEVICE_NAME_ALIASES[deviceIdOrKey] || deviceIdOrKey
    return getDeviceDisplayName(brandDeviceKeyFromDataKey(normalized))
}

export function getDeviceHeroImage(deviceKey) {
    const mod = deviceImages[deviceKey]
    if (mod) return mod.default || mod
    const device = getDeviceByKey(deviceKey)
    if (device && device.hero_image && /^https?:\/\//.test(device.hero_image))
        return device.hero_image
    return null
}

export function getCardHeroImage(card) {
    if (!card) return null
    return (
        (card.type === 'device' && card.slug && getKitHeroImage(card.slug)) ||
        getDeviceHeroImage(card.device) ||
        getAccessoryHeroImage(card.device) ||
        null
    )
}

export function getDeviceImage(deviceKey) {
    const mod = deviceInAppImages[deviceKey]
    if (mod) return mod.default || mod
    const device = getDeviceByKey(deviceKey)
    if (
        device &&
        device.device_image &&
        /^https?:\/\//.test(device.device_image)
    )
        return device.device_image
    return null
}

// Finds the best-matching image_range for a version, requiring `field` to be
// set on the rule. `from` is inclusive; `to` (when set) is inclusive; a blank
// `to` means open-ended. When several rules match, the one with the highest
// `from` wins (most specific). Returns the rule index, or -1.
function matchImageRangeIndex(ranges, version, field) {
    if (!Array.isArray(ranges) || !version) return -1
    const ver = String(version)
    let bestIdx = -1
    let bestFrom = null
    ranges.forEach((r, i) => {
        if (!r || !r[field]) return
        const from = r.from || '0'
        const fromOk = !isVersionAbove(from, ver) // from <= version
        const toOk = !r.to || !isVersionAbove(ver, r.to) // version <= to
        if (fromOk && toOk) {
            if (bestFrom === null || isVersionAbove(from, bestFrom)) {
                bestFrom = from
                bestIdx = i
            }
        }
    })
    return bestIdx
}

// Version-aware in-app device image. Resolution order: image_ranges (version
// windows, e.g. Klaw >= 1.0.3), then an exact per-version override (e.g. Crawlee
// v2.2), then the device-level image. Falls back cleanly so devices/brands
// without overrides are unaffected.
export function getDeviceImageForVersion(deviceKey, version) {
    const device = getDeviceByKey(deviceKey)
    if (device && version) {
        const ri = matchImageRangeIndex(
            device.image_ranges,
            version,
            'device_image'
        )
        if (ri >= 0) {
            const mod = deviceInAppImages[`${deviceKey}#r${ri}`]
            if (mod) return mod.default || mod
            const img = device.image_ranges[ri].device_image
            if (img && /^https?:\/\//.test(img)) return img
        }
        if (Array.isArray(device.versions)) {
            const v = device.versions.find(
                (x) =>
                    x.slug === String(version) ||
                    String(version).startsWith(x.slug)
            )
            if (v) {
                const mod = deviceInAppImages[`${deviceKey}@${v.slug}`]
                if (mod) return mod.default || mod
                if (v.device_image && /^https?:\/\//.test(v.device_image))
                    return v.device_image
            }
        }
    }
    return getDeviceImage(deviceKey)
}

export function getDevicePinout(deviceKey) {
    const mod = devicePinoutImages[deviceKey]
    const device = getDeviceByKey(deviceKey)
    if (mod)
        if (deviceKey === 'Poki')
            return deviceInAppImages[deviceKey] || mod.default
        else return mod.default || mod
    if (
        device &&
        device.pinout_diagram &&
        /^https?:\/\//.test(device.pinout_diagram)
    )
        return device.pinout_diagram
    return null
}

function sessionDevice(connectedDevice, version) {
    const device = connectedDevice || sessionStorage.getItem('connectedDevice')
    const ver = version || sessionStorage.getItem('deviceVersion') || '1'
    if (!device) return null
    const dataKey = generateDeviceDataKey(device, ver)
    if (dataKey === 'HUMANOID2' && device !== 'Humanoid') return null
    return { key: brandDeviceKeyFromDataKey(dataKey), version: ver }
}

// Convenience wrapper: resolve the brand in-app device image straight from the
// runtime connectedDevice ('Humanoid', 'Hexapod', 'Ace' ...) and its version,
// handling the data-key -> brand-key mapping. Returns null for the default
// brand (bundled art) so callers keep their own version-aware fallback.
export function getConnectedDeviceImage(connectedDevice, version) {
    const d = sessionDevice(connectedDevice, version)
    return d ? getDeviceImageForVersion(d.key, d.version) : null
}

export function withBrandDeviceImage(bundled, connectedDevice, version) {
    return getConnectedDeviceImage(connectedDevice, version) || bundled
}

export function withBrandPinout(bundled, connectedDevice, version) {
    return getConnectedDevicePinout(connectedDevice, version) || bundled
}

export function getVersionedDeviceName(connectedDevice, version) {
    const d = sessionDevice(connectedDevice, version)
    const device = d && getDeviceByKey(d.key)
    return device ? device.display_name : getBrandDeviceName(connectedDevice)
}

export function getConnectedDevicePinout(connectedDevice, version) {
    const d = sessionDevice(connectedDevice, version)
    return d ? getDevicePinoutForVersion(d.key, d.version) : null
}

// Version-aware pinout diagram. Same resolution order as
// getDeviceImageForVersion: image_ranges, then exact per-version, then
// device-level pinout.
export function getDevicePinoutForVersion(deviceKey, version) {
    const device = getDeviceByKey(deviceKey)
    if (device && version) {
        const ri = matchImageRangeIndex(
            device.image_ranges,
            version,
            'pinout_diagram'
        )
        if (ri >= 0) {
            const mod = devicePinoutImages[`${deviceKey}#r${ri}`]
            if (mod) return mod.default || mod
            const img = device.image_ranges[ri].pinout_diagram
            if (img && /^https?:\/\//.test(img)) return img
        }
        if (Array.isArray(device.versions)) {
            const v = device.versions.find(
                (x) =>
                    x.slug === String(version) ||
                    String(version).startsWith(x.slug)
            )
            if (v) {
                const mod = devicePinoutImages[`${deviceKey}@${v.slug}`]
                if (mod) return mod.default || mod
                if (v.pinout_diagram && /^https?:\/\//.test(v.pinout_diagram))
                    return v.pinout_diagram
            }
        }
    }
    return getDevicePinout(deviceKey)
}

// Brand display name for the currently connected device (reads sessionStorage
// connectedDevice + deviceVersion). Use this anywhere a hardcoded device name
// (PeeCee/Zing/Crawl-e/Klaw-b...) was shown to the user.
export function getConnectedDeviceName() {
    const cd = sessionStorage.getItem('connectedDevice') || 'Ace'
    const ver = sessionStorage.getItem('deviceVersion') || '1'
    return getDeviceDisplayName(
        brandDeviceKeyFromDataKey(generateDeviceDataKey(cd, ver))
    )
}
export function getConnectedDeviceNameWithVersion() {
    const cd = sessionStorage.getItem('connectedDevice') || 'Ace'
    const ver = sessionStorage.getItem('deviceVersion') || '1'
    const name = getDeviceDisplayName(
        brandDeviceKeyFromDataKey(generateDeviceDataKey(cd, ver))
    )
    const versionLabel = `(v${(ver || '1.0').split('.').slice(0, 3).join('.')})`
    return `${name} ${versionLabel}`
}

export function isDeviceEnabled(deviceKey) {
    const device = getDeviceByKey(deviceKey)
    return device ? device.enabled : false
}

export function getDeviceVersions(deviceKey) {
    const device = getDeviceByKey(deviceKey)
    return (device && device.versions) || []
}

export function getEnabledDeviceVersions(deviceKey) {
    return getDeviceVersions(deviceKey).filter((v) => v.enabled !== false)
}

export function getEnabledCodingModes(deviceKey) {
    return (brandConfig.codingModes || []).filter((mode) => {
        if (!mode.enabled) return false
        if (deviceKey && mode.device_gating && mode.device_gating.exclude) {
            return !mode.device_gating.exclude.includes(deviceKey)
        }
        return true
    })
}

export function isCodingModeEnabled(modeSlug, deviceKey) {
    const mode = (brandConfig.codingModes || []).find(
        (m) => m.slug === modeSlug
    )
    if (!mode || !mode.enabled) return false
    if (deviceKey && mode.device_gating && mode.device_gating.exclude) {
        return !mode.device_gating.exclude.includes(deviceKey)
    }
    return true
}

export function getCodingMode(modeSlug) {
    return (brandConfig.codingModes || []).find((m) => m.slug === modeSlug)
}

// Device applicability only (device_gating.exclude), ignoring the enabled flag.
// Used to decide whether a mode should appear at all for a device. A mode that
// is applicable but disabled should still be shown (greyed), so this is kept
// separate from isCodingModeEnabled.
export function isCodingModeAvailableForDevice(modeSlug, deviceKey) {
    const mode = getCodingMode(modeSlug)
    if (!mode) return false
    if (deviceKey && mode.device_gating && mode.device_gating.exclude) {
        if (mode.device_gating.exclude.includes(deviceKey)) return false
    }
    if (deviceKey && Array.isArray(mode.videos) && mode.videos.length) {
        if (getModeVideos(modeSlug, deviceKey).length === 0) return false
    }
    return true
}

export function getCodingFeatures(modeSlug) {
    const mode = getCodingMode(modeSlug)
    return (mode && mode.features) || []
}

// Sub-feature gating inside a coding mode (e.g. code -> python, play -> music,
// data -> ultrasonic). Each feature can be turned off globally (enabled:false)
// and scoped to specific devices (device_scope). Lenient: when a mode declares
// no features, or the slug is not listed, the feature is shown; an empty/absent
// device_scope means the feature applies to every device. deviceKey is a brand
// device key (Ace1, Humanoid, Toki ...) as used in devices.yml.
export function isCodingFeatureEnabled(modeSlug, featureSlug, deviceKey) {
    const mode = getCodingMode(modeSlug)
    if (!mode || !mode.enabled) return false
    const features = mode.features
    if (!Array.isArray(features) || !features.length) return true
    const feature = features.find((f) => f.slug === featureSlug)
    if (!feature) return true
    if (feature.enabled === false) return false
    if (
        deviceKey &&
        Array.isArray(feature.device_scope) &&
        feature.device_scope.length
    ) {
        return feature.device_scope.includes(deviceKey)
    }
    return true
}

// Learning videos on a coding mode (Make mode). Gated like features, and
// entries without a youtube_url are dropped so placeholders stay invisible.
export function getModeVideos(modeSlug, deviceKey) {
    const mode = getCodingMode(modeSlug)
    if (!mode) return []
    return (mode.videos || []).filter((video) => {
        if (video.enabled === false) return false
        if (!video.youtube_url) return false
        if (
            deviceKey &&
            Array.isArray(video.device_scope) &&
            video.device_scope.length
        ) {
            return video.device_scope.includes(deviceKey)
        }
        return true
    })
}

export function getEnabledInternalAccessories(deviceKey) {
    return (brandConfig.internalAccessories || []).filter((acc) => {
        if (!acc.enabled) return false
        if (deviceKey && acc.device_scope) {
            return acc.device_scope.includes(deviceKey)
        }
        return true
    })
}

export function isInternalAccessoryEnabled(slug) {
    const acc = (brandConfig.internalAccessories || []).find(
        (a) => a.slug === slug
    )
    return acc ? acc.enabled : false
}

export function isInternalAccessoryEnabledForDevice(slug, deviceKey) {
    const acc = (brandConfig.internalAccessories || []).find(
        (a) => a.slug === slug
    )
    if (!acc || !acc.enabled) return false
    // only restrict by device when an explicit, non-empty scope is set
    if (
        deviceKey &&
        Array.isArray(acc.device_scope) &&
        acc.device_scope.length
    ) {
        return acc.device_scope.includes(deviceKey)
    }
    return true
}

export function getEnabledExternalAccessories(deviceKey) {
    return (brandConfig.externalAccessories || []).filter((acc) => {
        if (!acc.enabled) return false
        if (deviceKey && acc.device_scope) {
            return acc.device_scope.includes(deviceKey)
        }
        return true
    })
}

export function isExternalAccessoryEnabled(slug) {
    const acc = (brandConfig.externalAccessories || []).find(
        (a) => a.slug === slug
    )
    return acc ? acc.enabled : false
}

// Returns a brand-specific accessory image only (an uploaded/URL image that the
// brand set). Bundled default images (src/Assets/Bisoft_UI/...) return null so
// the screen keeps its device-aware renderCompImg artwork.
export function getExternalAccessoryImage(slug) {
    const mod = externalImages[slug]
    if (mod) return mod.default || mod
    const acc = (brandConfig.externalAccessories || []).find(
        (a) => a.slug === slug
    )
    if (acc && acc.image && /^https?:\/\//.test(acc.image)) return acc.image
    return null
}

export function getExternalAccessoryImageByType(type) {
    const slug = EXTERNAL_TYPE_TO_SLUG[type]
    if (!slug) return null
    return getExternalAccessoryImage(slug)
}

// The concept/data.js component lists are keyed by data keys (ACE1, TOKI1,
// HUMANOID2 ...). The brand config device_scope uses brand device keys
// (Ace1, Toki, Humanoid ...). This maps between them.
const DATA_KEY_TO_DEVICE = {
    ACE1: 'Ace1',
    ACE0: 'Ace0',
    ROBOKI1: 'Roboki',
    MOKI1: 'Moki',
    TOKI1: 'Toki',
    POKI1: 'Poki',
    HUMANOID0: 'Humanoid',
    HUMANOID1: 'Humanoid',
    HUMANOID2: 'Humanoid',
    HUMANOID3: 'Humanoid',
    HEXAPOD1: 'Hexapod',
    HEXAPOD2: 'Hexapod',
    HEXAPOD3: 'Hexapod',
    KLAW1: 'Klaw',
    ATR1: 'Atr',
    WOOFKIT1: 'Poki',
}

export function brandDeviceKeyFromDataKey(dataKey) {
    return DATA_KEY_TO_DEVICE[dataKey] || dataKey
}

export function isExternalAccessoryEnabledForDevice(slug, deviceKey) {
    const acc = (brandConfig.externalAccessories || []).find(
        (a) => a.slug === slug
    )
    if (!acc || !acc.enabled) return false
    // only restrict by device when an explicit, non-empty scope is set
    if (
        deviceKey &&
        Array.isArray(acc.device_scope) &&
        acc.device_scope.length
    ) {
        return acc.device_scope.includes(deviceKey)
    }
    return true
}

// Maps every component `type` used in concept/data.js to an external-accessory
// slug in the brand config. This map must stay complete: any type missing here
// is treated as not enabled and hidden, so new component types must be added
// both here and to the admin panel (plode-admin/admin.js).
const EXTERNAL_TYPE_TO_SLUG = {
    led: 'led',
    led_1c: 'led',
    led_strip: 'led',
    laser: 'laser',
    RGB: 'rgb-led',
    servo_motor: 'servo-motor',
    servo_motor_270: 'servo-motor',
    servo_motor_360: 'servo-motor',
    servo_motor_sg90: 'servo-motor',
    serial_servo_motor: 'servo-motor',
    dc_motor: 'dc-motor',
    geared_motor: 'mini-geared-motor',
    mini_geared_motor: 'mini-geared-motor',
    dynamex_motor: 'dynamex-motor',
    motor_driver: 'motor-driver',
    pc_motor_driver: 'motor-driver',
    L293D_motor_driver: 'motor-driver',
    '4channel_relay': 'relay',
    '4_CH_relay': 'relay',
    electromagnet: 'electromagnet',
    OLED: 'oled-display',
    dot_matrix: 'dot-matrix',
    mp3: 'mp3-player',
    Audio_codec: 'speaker',
    speaker: 'speaker',
    beeper: 'beeper',
    tern_beeper: 'beeper',
    solar_panel: 'solar-panel',
    touch_sensor: 'touch-switch',
    tern_touch_sensor: 'touch-switch',
    capacitative_touch_sensor: 'touch-switch',
    tact_switch: 'tact-switch',
    tact_switch_2c: 'tact-switch',
    tern_tact_switch: 'tact-switch',
    dip_switch: 'dip-switch',
    dual_switch: 'dual-switch',
    ultrasonic_sensor: 'ultrasonic-sensor',
    ultrasonic_sensor_v1: 'ultrasonic-sensor-v2',
    distance_sensor: 'ultrasonic-sensor',
    IR_proximity: 'ir-sensor-ext',
    pir_sensor: 'pir-sensor',
    PIR_sensor: 'pir-sensor',
    temperature_sensor: 'temperature-sensor',
    DHT11: 'temperature-sensor',
    humidity: 'temperature-sensor',
    tern_humidity_sensor: 'temperature-sensor',
    gas: 'gas-sensor',
    tern_gas_sensor: 'gas-sensor',
    light_sensor: 'light-sensor',
    LDR_sensor: 'light-sensor',
    Photo_diode: 'light-sensor',
    rain_sensor: 'rain-sensor',
    rain_sensor_module_digital: 'rain-sensor',
    rain_sensor_module_analog: 'rain-sensor',
    metal_detector: 'metal-detector',
    sound_sensor: 'sound-sensor',
    tern_sound_sensor: 'sound-sensor',
    color_sensor: 'color-sensor-ext',
    Color_sensor: 'color-sensor-ext',
    rfid: 'rfid',
    joystick: 'joystick',
    joystick_v2: 'joystick-v2',
    joystickController: 'joystick-controller',
    rotational_sensor: 'rotary-encoder',
    Magnetic_encoder: 'rotary-encoder',
    pot: 'potentiometer',
    linear_pot: 'potentiometer',
    IMU: 'imu',
    Magnetometer: 'magnetometer',
    compass: 'magnetometer',
    gyro_sensor: 'gyroscope-ext',
    accelerometer: 'accelerometer-ext',
    '4_in_1_sensor': 'four-in-one-sensor-ext',
    gesture_sensor: 'gesture-sensor-ext',
    battery: 'battery-ext',
    Digital_3_Pin_Output: 'digital-output',
    Digital_2_Pin_Output: 'digital-output',
    Digital_Output_module: 'digital-output',
    Digital_3_Pin_Input: 'digital-input',
    Digital_2_Pin_Input: 'digital-input',
    Digital_sensor_module: 'digital-input',
    PWM_3_Pin_Output: 'pwm-output',
    PWM_2_Pin_Output: 'pwm-output',
    PWM_output_module: 'pwm-output',
    Analog_3_Pin_Input: 'analog-input',
    Analog_sensor_module: 'analog-input',
    Analog_sensor_component: 'analog-input',
    dual_splitter: 'splitter',
    extender: 'splitter',
    play_shield: 'shield',
    play_shield_v1: 'shield',
    arm_shield: 'shield',
    peeCee_ATR: 'shield',
    pcAiTool: 'ai-tool',
}

export function isExternalAccessoryTypeEnabled(type, deviceKey) {
    const slug = EXTERNAL_TYPE_TO_SLUG[type]
    if (!slug) return false
    return isExternalAccessoryEnabledForDevice(slug, deviceKey)
}

// Web AI "Code" screen feature catalog. Categories (Inputs, Communication,
// Outputs, A.I.) each hold a list of features with an enabled flag, so features
// can be whitelabeled (enabled/disabled) per brand from ai-code-features.yml.
export function getAiCodeCategories() {
    return (brandConfig.aiCodeFeatures || []).filter((c) => c.enabled !== false)
}

export function getAiCodeFeatures(categorySlug) {
    const category = (brandConfig.aiCodeFeatures || []).find(
        (c) => c.slug === categorySlug
    )
    return (category && category.features) || []
}

export function isAiCodeFeatureEnabled(categorySlug, featureSlug) {
    const feature = getAiCodeFeatures(categorySlug).find(
        (f) => f.slug === featureSlug
    )
    return feature ? feature.enabled !== false : false
}

export function getLogo(variant = 'primary') {
    let logo
    if (variant === 'compact') logo = logo_compact || defaultLogoCompact
    else if (variant === 'company') logo = logo_company || defaultLogoCompany
    else if (variant === 'company_compact')
        logo = logo_company_compact || defaultLogoCompanyCompact
    else if (variant === 'icon_svg') logo = logo_icon_svg || defaultLogoIconSvg
    else if (variant === 'favicon') logo = logo_favicon || defaultLogoIconSvg
    else logo = logo_primary || defaultLogoPrimary
    if (typeof logo === 'object' && logo !== null) return logo.default || logo
    return logo
}

export function getAppName() {
    return brandConfig.brand.display_name
}

export function getSplashConfig() {
    return brandConfig.brand.splash
}

export function getHomeDescription() {
    const home = brandConfig.brand.home
    return (home && home.description) || null
}

export function getHomeIllustration() {
    const img = home_illustration || defaultHomeIllustration
    if (typeof img === 'object' && img !== null) return img.default || img
    return img
}

export function isAppDownloadEnabled() {
    const home = brandConfig.brand.home
    return !home || home.show_app_download !== false
}

export function getLegalInfo() {
    return brandConfig.brand.legal
}

export default brandConfig
